import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Target, TrendingUp, Calendar, User, ChevronRight } from 'lucide-react';
import { OnboardingProject } from './types';
import { mockProjects } from './mockData';

interface ProjectPickerProps {
  onProjectSelect: (project: OnboardingProject) => void;
}

export const ProjectPicker: React.FC<ProjectPickerProps> = ({ onProjectSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [projects] = useState<OnboardingProject[]>(mockProjects);

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.clientName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Premium Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white to-gray-50 flex items-center justify-center shadow-lg mr-4 border border-gray-200">
              <img 
                src="/tyler-technologies-logo.png" 
                alt="Tyler Technologies Logo" 
                className="w-14 h-14 object-contain drop-shadow-sm"
                onError={(e) => console.error('Tyler Technologies logo failed to load:', e)}
              />
            </div>
            <div className="text-left">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                Launch Pad
              </h1>
              <p className="text-slate-400 text-lg">
                Tyler Technologies Premium Portal
              </p>
            </div>
          </div>
          <p className="text-slate-300 text-xl max-w-2xl mx-auto">
            Select a project to access the premium client portal and PM cockpit
          </p>
        </div>

        {/* Premium Search */}
        <div className="mb-8">
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 tyler-card border border-[#4267A1]/30 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-[#4267A1] focus:shadow-lg focus:shadow-[#4267A1]/25 transition-all duration-200 text-center"
            />
          </div>
        </div>

        {/* Premium Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              whileHover={{ scale: 1.02 }}
              onClick={() => onProjectSelect(project)}
              className="tyler-card-premium p-8 cursor-pointer hover:border-[#4267A1]/50 transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">{project.name}</h3>
                <div className={`w-4 h-4 rounded-full shadow-lg ${
                  project.healthScore >= 80 ? 'bg-gradient-to-r from-[#8A9150] to-[#A5B366] shadow-[#8A9150]/25' :
                  project.healthScore >= 60 ? 'bg-gradient-to-r from-yellow-500 to-orange-500 shadow-yellow-500/25' :
                  'bg-gradient-to-r from-red-500 to-red-600 shadow-red-500/25'
                }`} />
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">Progress</span>
                    <span className="text-lg font-bold bg-gradient-to-r from-[#4267A1] to-[#8A9150] bg-clip-text text-transparent">{project.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-[#4267A1] via-[#5A7BC4] to-[#8A9150] h-3 rounded-full shadow-lg"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm tyler-card p-3">
                  <span className="text-slate-400">Owner</span>
                  <span className="text-white font-medium">{project.projectManager}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm tyler-card p-3">
                  <span className="text-slate-400">Target Date</span>
                  <span className="text-white font-medium">{new Date(project.targetDate).toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-[#4267A1]/20">
                <button className="w-full px-6 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 flex items-center justify-center">
                  <Target className="w-5 h-5 mr-2" />
                  Open Project
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
